import { type FC, useState } from "react";

import { faListUl } from "@fortawesome/pro-duotone-svg-icons/faListUl";
import { faPenToSquare } from "@fortawesome/pro-duotone-svg-icons/faPenToSquare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { type ConversationRefFragment } from "@app_schema";

import { pluralize } from "@application/utilities/pluralize";

import { Button } from "@styled/button";
import { CountButton } from "@styled/count_button";
import { DestroyDialog } from "@styled/destroy_dialog";
import { Notification } from "@styled/notification";
import { PillGroup, PillLabel } from "@styled/pill_group";
import { Sentence } from "@styled/sentence";

import { PreviousChatsMenu } from "./previous_chats_menu";
import { SuggestedQuestionsMenu } from "./suggested_questions_menu";
import { type AssistantChats } from "./use_assistant_chats";

// The assistant's half of the toolbar: Previous chats, New chat, Suggested
// questions when the assistant has any, and on Drafting the Sections toggle
// with its citations count, which shows them on its own. New chat never
// deletes; it's disabled while the chat is empty.
export const AssistantToolbar: FC<{
  chats: AssistantChats;
  available: boolean;
  sections?: {
    open: boolean;
    count: number;
    onToggle(): void;
    onShowCitations(): void;
  };
}> = ({ chats, available, sections }) => {
  const [deleting, setDeleting] = useState<ConversationRefFragment | null>(
    null,
  );
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState(false);

  const close = () => {
    setDeleting(null);
    setFailed(false);
  };
  const confirm = async (id: string) => {
    setRemoving(true);
    const removed = await chats.remove(id);
    setRemoving(false);
    if (removed) close();
    else setFailed(true);
  };

  return (
    <>
      <PillGroup phone="icons">
        <PreviousChatsMenu
          chats={chats.chats}
          currentId={chats.current?.id}
          onSelect={chats.selectChat}
          onRename={(id, name) => void chats.rename(id, name)}
          onDelete={setDeleting}
        />
        <Button
          type="button"
          color="white"
          size="sm"
          onClick={chats.newChat}
          disabled={!chats.current}
        >
          <FontAwesomeIcon icon={faPenToSquare} />
          <PillLabel>New chat</PillLabel>
        </Button>
        {sections && (
          <Button
            type="button"
            color="white"
            size="sm"
            aria-pressed={sections.open}
            onClick={sections.onToggle}
          >
            <FontAwesomeIcon icon={faListUl} />
            <PillLabel>Sections</PillLabel>
          </Button>
        )}
        {sections && (
          <CountButton
            count={sections.count}
            label={`${String(sections.count)} ${pluralize(sections.count, "citation", "citations")} to check`}
            onClick={sections.onShowCitations}
          />
        )}
        {!sections && (
          <SuggestedQuestionsMenu
            questions={chats.questions}
            disabled={
              !available || !chats.canAsk || chats.running || chats.busy
            }
            onAsk={(question) => void chats.send({ message: question })}
          />
        )}
      </PillGroup>
      {deleting && (
        <DestroyDialog
          name={deleting.displayName}
          loading={removing}
          onClose={close}
          onConfirm={() => void confirm(deleting.id)}
        >
          {failed && (
            <Notification color="rose">
              <Sentence>Couldn't delete the chat. Try again.</Sentence>
            </Notification>
          )}
          Deleting “{deleting.displayName}” also deletes its messages.
        </DestroyDialog>
      )}
    </>
  );
};
